import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useSyncExternalStore } from 'react';

import { cn } from '../lib/cn';

type ToastKind = 'default' | 'error' | 'success';
interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}

/** Fire a transient toast (auto-dismisses after 4s). */
export function toast(message: string, kind: ToastKind = 'default'): void {
  const item: ToastItem = { id: nextId++, message, kind };
  items = [...items, item];
  emit();
  setTimeout(() => {
    items = items.filter((i) => i.id !== item.id);
    emit();
  }, 4_000);
}

/** Mount once near the app root. */
export function Toaster() {
  const current = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => items,
    () => items,
  );
  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
      {current.map((t) => (
        <div
          key={t.id}
          // Live-region semantics: errors interrupt (alert/assertive), the rest
          // announce politely. Status is never color-alone — each kind carries
          // its icon (docs/15 §6 / AGENTS.md UI rules).
          role={t.kind === 'error' ? 'alert' : 'status'}
          aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
          className={cn(
            // Rendered in a fixed overlay layer — surface and foreground travel together.
            'flex items-center gap-2 rounded-md border bg-raised px-4 py-2 text-sm text-foreground shadow-lg',
            t.kind === 'error' && 'border-destructive text-destructive',
            t.kind === 'success' && 'border-success text-success-tint-fg',
          )}
        >
          {t.kind === 'error' && <AlertCircle aria-hidden className="h-4 w-4 shrink-0" />}
          {t.kind === 'success' && <CheckCircle2 aria-hidden className="h-4 w-4 shrink-0" />}
          {t.message}
        </div>
      ))}
    </div>
  );
}
